import React, { useCallback } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { APP_NAME } from '../../config/branding';
import { useAuth } from '../../contexts/AuthContext';
import { markDeleteAccountReturn } from '../../core/navigation/deleteAccountReturn';
import { navigateToAuth, navigateToLanding, navigateToLegal } from '../../core/navigation/navigationRef';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { DeleteAccountConfirmFlow } from './DeleteAccountConfirmFlow';
import { LEGAL_UPDATED } from './legalCopy';
import { MarketingBackBar } from './MarketingBackBar';

const STEPS = [
  `In the ${APP_NAME} app, sign in, open the account menu, tap Settings, then Delete account at the bottom.`,
  `Or open this page in a browser and sign in with the Google, Apple, or phone login on the account.`,
  'Complete all three confirmations: wait through the first screen, turn on every acknowledgement, then type your email or phone and DELETE.',
];

/** In-app (and web) account deactivation — same soft-delete API on every platform. */
export function DeleteAccountScreen() {
  const insets = useSafeAreaInsets();
  const { isAuthenticated, isLoading, user, logout } = useAuth();

  const startSignIn = useCallback(() => {
    markDeleteAccountReturn();
    navigateToAuth();
  }, []);

  return (
    <View style={styles.root}>
      <MarketingBackBar title="Delete account" />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          { paddingBottom: Math.max(insets.bottom, spacing.xl) + spacing.xl },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <Text style={styles.kicker}>{APP_NAME}</Text>
        <Text style={styles.title}>Delete your {APP_NAME} account</Text>
        <Text style={styles.updated}>Updated {LEGAL_UPDATED}</Text>

        <View style={styles.highlight}>
          <Text style={styles.highlightText}>
            You can deactivate a {APP_NAME} account (operated by FMA) in this app — Settings,
            then Delete account at the bottom — or on this page. You must sign in. We
            soft-delete only: sign-in is turned off. Associated records are kept and never
            permanently erased.
          </Text>
        </View>

        <Text style={styles.sectionTitle}>How to request that your {APP_NAME} account is deleted</Text>
        <View style={styles.steps}>
          {STEPS.map((step, index) => (
            <View key={step} style={styles.listRow}>
              <Text style={styles.bullet}>{index + 1}.</Text>
              <Text style={styles.stepText}>{step}</Text>
            </View>
          ))}
        </View>

        <DeleteAccountConfirmFlow
          isAuthenticated={isAuthenticated}
          isLoading={isLoading}
          user={user}
          logout={logout}
          onSignIn={startSignIn}
          onDone={navigateToLanding}
        />

        <Text style={styles.sectionTitle}>What we deactivate</Text>
        <Text style={styles.body}>
          Sign-in on Android, iOS, and the web. Push notifications. In-app display of this
          account’s profile, dashboards, imports, chats, and alerts.
        </Text>
        <Text style={styles.sectionTitle}>What we keep (never hard-deleted)</Text>
        <Text style={styles.body}>
          Email, phone, name, and sign-in IDs; financial records you uploaded or entered;
          household history; store and payment records; security and server logs.
        </Text>
        <Text style={styles.sectionTitle}>Retention</Text>
        <Text style={styles.body}>
          Deactivation is immediate after the third confirmation. Soft-deleted records stay stored
          internally. We do not run a permanent wipe. Backups and logs may retain copies for up to
          90 days. Apple, Google, and Stripe keep their own records.
        </Text>

        <View style={styles.footerLinks}>
          <FooterLink label="Privacy" onPress={() => navigateToLegal('Privacy')} />
          <FooterLink label="Terms" onPress={() => navigateToLegal('Terms')} />
          <FooterLink label="Support" onPress={() => navigateToLegal('Support')} />
        </View>
      </ScrollView>
    </View>
  );
}

function FooterLink({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable
      accessibilityRole="link"
      onPress={onPress}
      style={({ pressed }) => [styles.footerLink, pressed && styles.pressed]}
    >
      <Text style={styles.footerLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroll: {
    flex: 1,
  },
  content: {
    paddingTop: spacing.lg,
    paddingHorizontal: spacing.lg,
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
    gap: spacing.md,
  },
  kicker: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
  },
  title: {
    ...typography.title,
    color: colors.text,
  },
  updated: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.sm,
  },
  highlight: {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    borderLeftWidth: 3,
    borderLeftColor: colors.warning,
    borderRadius: radii.sm,
    padding: spacing.md,
  },
  highlightText: {
    ...typography.body,
    color: colors.text,
    lineHeight: 24,
    fontWeight: '600',
  },
  sectionTitle: {
    ...typography.headline,
    fontSize: 16,
    color: colors.text,
    marginTop: spacing.sm,
  },
  body: {
    ...typography.body,
    color: colors.textMuted,
    lineHeight: 24,
  },
  steps: {
    gap: spacing.sm,
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.28)',
    borderRadius: radii.md,
    padding: spacing.md,
  },
  listRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'flex-start',
  },
  bullet: {
    ...typography.body,
    color: colors.accent,
    lineHeight: 24,
    minWidth: 22,
    fontWeight: '600',
  },
  stepText: {
    ...typography.body,
    color: colors.text,
    lineHeight: 24,
    fontWeight: '500',
    flex: 1,
  },
  footerLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    marginTop: spacing.lg,
  },
  footerLink: {
    minHeight: 44,
    justifyContent: 'center',
    cursor: 'pointer',
  } as ViewStyle,
  footerLinkText: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textMuted,
  },
  pressed: {
    opacity: 0.85,
  },
});
